import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft, Check, Star, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useLang } from '../contexts/LanguageContext';
import { api } from '../lib/api';
import t from '../translations';

export default function PaywallPage() {
  const navigate      = useNavigate();
  const { profile, user } = useAuth();
  const { lang }      = useLang();
  const tr            = t[lang];

  const [selected,    setSelected]    = useState('yearly');
  const [promoCode,   setPromoCode]   = useState('');
  const [promoMsg,    setPromoMsg]    = useState(null);
  const [promoLoading,setPromoLoading]= useState(false);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [pricing,     setPricing]     = useState(null);

  useEffect(() => {
    api.get('/api/pricing').then(d => setPricing(d)).catch(() => {
      // Fallback pricing
      setPricing({
        monthly: { price: '₹99', period: '/month', amountPaise: 9900 },
        yearly:  { price: '₹799', period: '/year', amountPaise: 79900, badge: 'Best Value', saving: 'Save ₹389' },
        trialDays: 7,
      });
    });
  }, []);

  const validatePromo = async () => {
    if (!promoCode.trim()) return;
    setPromoLoading(true);
    setPromoMsg(null);
    try {
      const d = await api.get(`/api/payment/promo/validate?code=${promoCode.trim()}`);
      setPromoMsg({ ok: true, text: d.description });
    } catch (err) {
      setPromoMsg({ ok: false, text: err.message || 'Invalid or expired code' });
    }
    setPromoLoading(false);
  };

  const handleSubscribe = async () => {
    if (!user) { navigate('/auth'); return; }
    setError('');
    setLoading(true);
    try {
      // Load Razorpay if not present
      if (!window.Razorpay) {
        await new Promise((res, rej) => {
          const s = document.createElement('script');
          s.src = 'https://checkout.razorpay.com/v1/checkout.js';
          s.onload = res;
          s.onerror = () => rej(new Error('Could not load payment gateway. Please check your connection.'));
          document.body.appendChild(s);
        });
      }

      const order = await api.post('/api/payment/create-order', {
        plan: selected,
        promoCode: promoCode.trim() || null,
      });

      const planLabel = pricing?.[selected]?.price + pricing?.[selected]?.period || selected;

      await new Promise((resolve, reject) => {
        const options = {
          key: order.keyId,
          amount: order.amount,
          currency: order.currency || 'INR',
          name: 'DailyRaashi',
          description: selected === 'yearly' ? '1 Year Subscription' : '1 Month Subscription',
          order_id: order.orderId,
          handler: async (response) => {
            try {
              await api.post('/api/payment/verify', {
                orderId:   response.razorpay_order_id,
                paymentId: response.razorpay_payment_id,
                signature: response.razorpay_signature,
                plan:      selected,
              });
              resolve();
            } catch (verifyErr) {
              reject(new Error('Payment verification failed. Please contact support if amount was deducted.'));
            }
          },
          modal: {
            ondismiss: () => reject(new Error('__cancelled__')),
            confirm_close: false,
          },
          prefill: {
            name:  profile?.name  || '',
            email: profile?.email || '',
          },
          theme: { color: '#C9A84C' },
          retry: { enabled: true, max_count: 3 },
        };
        const rzp = new window.Razorpay(options);
        rzp.on('payment.failed', (resp) => {
          reject(new Error(resp?.error?.description || 'Payment failed. Please try again.'));
        });
        rzp.open();
      });

      // Success
      navigate('/home');
    } catch (err) {
      if (err.message === '__cancelled__') {
        // User dismissed — just reset loading, no error
      } else {
        setError(err.message || tr.paymentFailed || 'Payment failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const features = [
    'Daily personalised Vedic reading',
    'Colour, food & routine guidance',
    'Dasha-aware predictions from Jyoti',
    "Today's Muhurtas & Chaughadiyas",
    'Cosmic planetary snapshot',
    'Vedic baby name generator',
    'Hindi & English support',
  ];

  const monthlyPrice = pricing?.monthly?.price || '₹99';
  const yearlyPrice  = pricing?.yearly?.price  || '₹799';
  const saving       = pricing?.yearly?.saving  || 'Save ₹389';

  return (
    <div style={{ minHeight: '100vh', background: '#0A0F1E', paddingBottom: 48 }}>

      <div style={{ padding: '56px 20px 20px', display: 'flex', alignItems: 'center', gap: 12 }}>
        <button onClick={() => navigate(-1)}
          style={{ background: 'none', border: 'none', color: '#C9A84C', cursor: 'pointer', display: 'flex', alignItems: 'center', padding: 0 }}>
          <ChevronLeft size={22} />
        </button>
        <div>
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: 24, color: '#F5F0E8', fontWeight: 700 }}>
            {tr.pricingTitle || 'Choose Your Plan'}
          </h1>
          <p style={{ fontSize: 13, color: '#8A8580', marginTop: 2 }}>
            {tr.pricingSubtitle || 'Unlock personalised Vedic guidance daily'}
          </p>
        </div>
      </div>

      <div style={{ padding: '0 20px' }}>

        {/* Trial banner */}
        <div style={{
          padding: '12px 16px', borderRadius: 12, marginBottom: 20,
          background: 'rgba(45,106,79,0.12)', border: '1px solid rgba(45,106,79,0.25)',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 16 }}>✦</span>
          <p style={{ fontSize: 13, color: '#F5F0E8' }}>
            <strong style={{ color: '#2D6A4F' }}>{pricing?.trialDays || 7}-day free trial</strong> included — no card charged until trial ends.
          </p>
        </div>

        {/* Plan Selector */}
        <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
          {/* Yearly */}
          <button data-testid="pricing-yearly-btn" onClick={() => setSelected('yearly')}
            style={{
              flex: 1, padding: '20px 16px', borderRadius: 16, cursor: 'pointer',
              border: `2px solid ${selected === 'yearly' ? '#C9A84C' : 'rgba(255,255,255,0.08)'}`,
              background: selected === 'yearly' ? 'rgba(201,168,76,0.08)' : '#111827',
              textAlign: 'left', position: 'relative', overflow: 'hidden',
            }}>
            <div style={{
              position: 'absolute', top: 10, right: 10,
              background: '#C9A84C', color: '#0A0F1E',
              fontSize: 9, fontWeight: 700, padding: '3px 8px', borderRadius: 8,
              textTransform: 'uppercase', display: 'flex', alignItems: 'center', gap: 4,
            }}>
              <Star size={8} fill="#0A0F1E" /> Best Value
            </div>
            <p style={{ fontSize: 12, color: selected === 'yearly' ? '#C9A84C' : '#8A8580', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>
              Yearly
            </p>
            <p style={{ fontSize: 26, fontWeight: 700, color: '#F5F0E8', fontFamily: "'Playfair Display', serif", marginBottom: 2 }}>
              {yearlyPrice}
            </p>
            <p style={{ fontSize: 11, color: '#8A8580' }}>per year</p>
            <p style={{ fontSize: 10, color: '#2D6A4F', marginTop: 4, fontWeight: 600 }}>{saving}</p>
          </button>

          {/* Monthly */}
          <button data-testid="pricing-monthly-btn" onClick={() => setSelected('monthly')}
            style={{
              flex: 1, padding: '20px 16px', borderRadius: 16, cursor: 'pointer',
              border: `2px solid ${selected === 'monthly' ? '#C9A84C' : 'rgba(255,255,255,0.08)'}`,
              background: selected === 'monthly' ? 'rgba(201,168,76,0.08)' : '#111827',
              textAlign: 'left',
            }}>
            <p style={{ fontSize: 12, color: selected === 'monthly' ? '#C9A84C' : '#8A8580', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>
              Monthly
            </p>
            <p style={{ fontSize: 26, fontWeight: 700, color: '#F5F0E8', fontFamily: "'Playfair Display', serif", marginBottom: 2 }}>
              {monthlyPrice}
            </p>
            <p style={{ fontSize: 11, color: '#8A8580' }}>per month</p>
          </button>
        </div>

        {/* Features */}
        <div className="card" style={{ marginBottom: 20 }}>
          <p style={{ fontSize: 12, color: '#C9A84C', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 14 }}>
            Everything Included
          </p>
          {features.map((f, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderBottom: i < features.length - 1 ? '1px solid rgba(255,255,255,0.04)' : 'none' }}>
              <div style={{ width: 20, height: 20, borderRadius: 6, background: 'rgba(45,106,79,0.2)', border: '1px solid rgba(45,106,79,0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Check size={12} color="#2D6A4F" />
              </div>
              <p style={{ fontSize: 13, color: '#F5F0E8' }}>{f}</p>
            </div>
          ))}
        </div>

        {/* Promo Code */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', gap: 10 }}>
            <input data-testid="promo-input" type="text" className="input-field"
              placeholder="Promo code" value={promoCode}
              onChange={e => { setPromoCode(e.target.value.toUpperCase()); setPromoMsg(null); }}
              style={{ flex: 1, letterSpacing: '0.08em' }} />
            <button data-testid="apply-promo-btn" onClick={validatePromo}
              disabled={promoLoading || !promoCode.trim()}
              style={{
                padding: '0 18px', background: 'rgba(201,168,76,0.1)',
                border: '1px solid rgba(201,168,76,0.3)', borderRadius: 12,
                color: '#C9A84C', cursor: 'pointer', fontWeight: 600, fontSize: 13,
                opacity: !promoCode.trim() ? 0.5 : 1, flexShrink: 0,
              }}>
              {promoLoading ? '…' : 'Apply'}
            </button>
          </div>
          {promoMsg && (
            <p style={{ fontSize: 12, marginTop: 6, color: promoMsg.ok ? '#2D6A4F' : '#C0392B' }}>
              {promoMsg.ok ? '✓' : '✗'} {promoMsg.text}
            </p>
          )}
        </div>

        {/* Error */}
        {error && (
          <div style={{
            padding: '12px 16px', borderRadius: 12, marginBottom: 16,
            background: 'rgba(192,57,43,0.08)', border: '1px solid rgba(192,57,43,0.2)',
            display: 'flex', alignItems: 'flex-start', gap: 10,
          }}>
            <p style={{ fontSize: 13, color: '#C0392B', flex: 1 }}>{error}</p>
            <button onClick={() => { setError(''); handleSubscribe(); }}
              style={{ background: 'none', border: 'none', color: '#C9A84C', cursor: 'pointer', flexShrink: 0, display: 'flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600 }}>
              <RefreshCw size={13} /> Retry
            </button>
          </div>
        )}

        {/* Subscribe CTA */}
        <button data-testid="subscribe-btn" onClick={handleSubscribe}
          disabled={loading || !pricing}
          className="btn-primary pulse-gold"
          style={{ fontSize: 16, padding: '18px 28px' }}>
          {loading ? 'Opening payment…' : `Subscribe — ${selected === 'yearly' ? `${yearlyPrice}/yr` : `${monthlyPrice}/mo`}`}
        </button>

        <p style={{ fontSize: 11, color: '#8A8580', textAlign: 'center', marginTop: 14, lineHeight: 1.5 }}>
          Secure payment via Razorpay. Cancel anytime.<br />
          DailyRaashi offers guidance and entertainment only. Not a substitute for professional advice.
        </p>
      </div>
    </div>
  );
}
